import PropTypes from "prop-types";
import { useMemo } from "react";

import { Button, Collapse, Empty, Spin, Table, Tag } from "antd";
import { HistoryOutlined } from "@ant-design/icons";

import { formatReservationDate, formatReservationTime } from "../utils";
import {
    ACTOR_TYPE_TAG_COLORS,
    formatActivityActionLabel,
    formatActivityLogTakerEmail,
    formatActorTypeLabel,
    getActivityLogActorObjectId,
} from "./activityLogFormatters";
import useReservationActivityLogs from "./useReservationActivityLogs";

const ActivityLogTakerCell = ({ log, t }) => {
    const email = formatActivityLogTakerEmail(log, t);
    const objectId = getActivityLogActorObjectId(log);

    return (
        <div className="flex w-full min-w-[8.5rem] flex-col gap-0.5">
            <span className="fontRegular break-all text-xs leading-snug text-[#2F2F46]">{email}</span>
            <span className="fontRegular break-all font-mono text-[10px] leading-snug text-[#9b9ba5]">
                {objectId || t("notAvailable")}
            </span>
        </div>
    );
};

ActivityLogTakerCell.propTypes = {
    log: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

const ActivityLogPanelBody = ({ loading, error, logs, hasMore, loadingMore, loadMore, t }) => {
    const columns = useMemo(
        () => [
            {
                title: t("upcomingDetails.activityLog.columns.action"),
                dataIndex: "action",
                key: "action",
                width: 168,
                render: (_, record) => (
                    <span className="fontRegular text-xs text-[#2F2F46]">
                        {formatActivityActionLabel(record.action, t)}
                    </span>
                ),
            },
            {
                title: t("upcomingDetails.activityLog.columns.userType"),
                dataIndex: "actorType",
                key: "userType",
                width: 108,
                render: (_, record) => {
                    if (!record.actorType) {
                        return t("notAvailable");
                    }

                    const label = formatActorTypeLabel(record.actorType, t);

                    return (
                        <Tag
                            className="!m-0 !rounded-full !border-none !px-2.5 !py-0.5 !text-[11px] !font-medium"
                            color={ACTOR_TYPE_TAG_COLORS[record.actorType] ?? "default"}>
                            {label}
                        </Tag>
                    );
                },
            },
            {
                title: t("upcomingDetails.activityLog.columns.taker"),
                dataIndex: "taker",
                key: "taker",
                width: 200,
                onCell: () => ({
                    className: "!align-top !whitespace-normal",
                }),
                onHeaderCell: () => ({
                    className: "!whitespace-normal",
                }),
                render: (_, record) => <ActivityLogTakerCell log={record} t={t} />,
            },
            {
                title: t("upcomingDetails.activityLog.columns.shortDescription"),
                dataIndex: "description",
                key: "description",
                ellipsis: true,
                render: (description) => (
                    <span className="fontRegular text-xs text-[#5f5f6e]" title={description}>
                        {description || t("notAvailable")}
                    </span>
                ),
            },
            {
                title: t("upcomingDetails.activityLog.columns.time"),
                dataIndex: "createdAt",
                key: "time",
                width: 148,
                align: "right",
                render: (_, record) => {
                    const createdAt = record.createdAt || record.updatedAt;
                    if (!createdAt) {
                        return t("notAvailable");
                    }

                    return (
                        <span className="fontRegular whitespace-nowrap text-xs text-[#9b9ba5]">
                            {formatReservationDate(createdAt, "MMM D, YYYY")}
                            <br />
                            {formatReservationTime(createdAt)}
                        </span>
                    );
                },
            },
        ],
        [t],
    );

    if (loading) {
        return (
            <div className="flex h-[280px] items-center justify-center rounded-xl border border-[#f0f0f0] bg-[#FAFAFC]">
                <Spin />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-[280px] items-center justify-center rounded-xl border border-[#f0f0f0] bg-[#FAFAFC] p-4">
                <Empty description={t("upcomingDetails.activityLog.loadError")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
        );
    }

    if (logs.length === 0) {
        return (
            <div className="flex h-[280px] items-center justify-center rounded-xl border border-[#f0f0f0] bg-[#FAFAFC] p-4">
                <Empty description={t("upcomingDetails.activityLog.empty")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
        );
    }

    return (
        <div className="overflow-hidden rounded-xl border border-[#f0f0f0] bg-[#FAFAFC]">
            <Table
                className="reservation-activity-log-table [&_.ant-table-cell]:!py-2.5 [&_.ant-table-cell]:!whitespace-normal [&_.ant-table-thead>tr>th]:!bg-[#FAFAFC] [&_.ant-table-thead>tr>th]:!text-xs [&_.ant-table-thead>tr>th]:!font-medium [&_.ant-table-thead>tr>th]:!text-[#7f8c8d]"
                columns={columns}
                dataSource={logs}
                pagination={false}
                rowKey={(record) => record._id || `${record.action}-${record.createdAt}`}
                scroll={{ x: 720 }}
                size="small"
            />
            {hasMore ? (
                <div className="border-t border-[#f0f0f0] bg-[#FAFAFC] px-4 py-2 text-center">
                    <Button type="link" loading={loadingMore} onClick={loadMore} className="!text-[#5D5DF6]">
                        {t("upcomingDetails.activityLog.loadMore")}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

ActivityLogPanelBody.propTypes = {
    loading: PropTypes.bool.isRequired,
    error: PropTypes.bool.isRequired,
    logs: PropTypes.array.isRequired,
    hasMore: PropTypes.bool.isRequired,
    loadingMore: PropTypes.bool.isRequired,
    loadMore: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

const ReservationActivityLogSection = ({ reservationId, refreshKey, t }) => {
    const { logs, loading, loadingMore, hasMore, error, loadMore } = useReservationActivityLogs(
        reservationId,
        refreshKey,
    );

    if (!reservationId) {
        return null;
    }

    const collapseLabel = (
        <div className="flex min-w-0 items-center gap-2">
            <HistoryOutlined className="text-[#5D5DF6]" />
            <span className="fontSemi text-base text-[#2F2F46]">{t("upcomingDetails.activityLog.title")}</span>
            {!loading && logs.length > 0 ? (
                <Tag className="!m-0 !rounded-full !border-none !bg-[#F2F2FA] !px-2 !text-[11px] !text-[#5D5DF6]">
                    {logs.length}
                </Tag>
            ) : null}
        </div>
    );

    return (
        <section className="overflow-hidden rounded-2xl border border-[#f0f0f0] bg-white">
            <Collapse
                bordered={false}
                expandIconPosition="end"
                defaultActiveKey={[]}
                className="reservation-activity-log-collapse !bg-white [&_.ant-collapse-content-box]:!px-4 [&_.ant-collapse-content-box]:!pb-4 [&_.ant-collapse-expand-icon]:!text-[#9b9ba5] [&_.ant-collapse-header]:!items-center [&_.ant-collapse-header]:!gap-3 [&_.ant-collapse-header]:!px-4 [&_.ant-collapse-header]:!py-4 [&_.ant-collapse-item]:!border-none"
                items={[
                    {
                        key: "activity-log",
                        label: collapseLabel,
                        children: (
                            <ActivityLogPanelBody
                                error={error}
                                hasMore={hasMore}
                                loadMore={loadMore}
                                loading={loading}
                                loadingMore={loadingMore}
                                logs={logs}
                                t={t}
                            />
                        ),
                    },
                ]}
            />
        </section>
    );
};

ReservationActivityLogSection.propTypes = {
    reservationId: PropTypes.string,
    refreshKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    t: PropTypes.func.isRequired,
};

export default ReservationActivityLogSection;
